import { C8Node, cnl } from "csi/typings";
import { ObservableMap, computed, makeObservable, observable, runInAction } from "mobx";
import { AbstractMaterial, CompositionMaterialDetails } from "./BaseCompositionMaterial";
import { BaseCompositionStore, loadNodes } from "./BaseCompositionStore";

export interface BomMaterialDetails {
	url: cnl;
	percentage: number;
	baseCompositions: CompositionMaterialDetails[];
}

export class MaterialOnBOM extends AbstractMaterial {
	/** map of all technical compositions so that a query is only run if a new material is selected, otherwise it will be in the map */
	materialTechnicalCompositionNodeMap: ObservableMap<cnl, C8Node[]> = observable.map<cnl, C8Node[]>(null, { deep: false });

	/** Finds and returns the current Bill of Materials (BOM) material node based on the current URL. */
	get currentBOMMaterialNode(): C8Node {
		return this.baseCompositionStore.compositionNodes.find((composition) => composition.$URL === this.currentUrl);
	}

	getShowErrorBOMMaterial = (percentageIsInvalid: boolean): boolean => {
		const { searchKeyInBannerConditions } = this.baseCompositionStore;
		let showError = percentageIsInvalid && !!this.percentage;
		showError = showError || !this.percentage && searchKeyInBannerConditions("BannerSpecifyPercentagePerBOMMaterial");
		return showError;
	};

	/** Retrieves the name of the current BOM material node. */
	get currentBOMMaterialName(): string {
		return this.baseCompositionStore.loc.getNodeName(this.currentBOMMaterialNode);
	}

	/** Checks if the composition placement's percentage is invalid, returning true if it exists and is not 100%. */
	get percentageIsInvalid(): boolean {
		let amount = 0;
		this.baseCompositionMaterials.forEach((compMat) => {
			amount += compMat.percentage;
		});
		return (this.baseCompositionStore.enforceHundredPercent || this.baseCompositionStore.fromStyleBom) && amount !== 100;
	}

	/**
	 * @override
	 * Clears the base composition material map and loads materials based on the current material attributes node.
	 * */
	override endSetUrlProcess = async (): Promise<void> => {
		this.baseCompositionMaterialMap.clear();
		await this.insertTechnicalCompositionNodesIfDoesntExist(this.currentUrl);
		const technicalCompositionNodes = this.materialTechnicalCompositionNodeMap.get(this.currentUrl);
		technicalCompositionNodes.forEach((technicalCompositionNode) => {
			this.loadBaseCompositionMaterial(
				technicalCompositionNode.Percentage * 100,
				technicalCompositionNode.Composition,
				technicalCompositionNode.SustainabilityCompositionType,
				technicalCompositionNode.ExemptFromPercentage,
			);
		});
	};

	/** if a query is needed perform one, otherwise do nothing */
	async insertTechnicalCompositionNodesIfDoesntExist(materialCNL: cnl): Promise<void> {
		if (!this.materialTechnicalCompositionNodeMap.has(materialCNL)) {
			const response = await loadNodes("GetTechnicalCompositionNodes", { Material: materialCNL });
			const technicalCompositionNodes = response.getResultNodes();
			runInAction(() => {
				this.materialTechnicalCompositionNodeMap.set(materialCNL, technicalCompositionNodes);
			});
		}
	}

	@computed get bomMaterialDetails(): BomMaterialDetails {
		return {
			url: this.currentUrl,
			percentage: this.percentage,
			baseCompositions: this.baseCompositionMaterials.map((bc) => bc.compositionMaterialDetails),
		};
	}

	constructor(baseCompositionStore: BaseCompositionStore, key: string, percentage?: number, currentUrl?: cnl) {
		super(baseCompositionStore, key, percentage, currentUrl);
		makeObservable(this);
	}
}